import React, { useState, useEffect } from 'react';
import { formatMoney } from '../utils/dealExtras';
import { theme } from '../theme';
import { getWatchQuote } from '../api/atlast';
import { usdParen } from '../utils/fee';

// Extracted into its own file (Sep 8, per Ali's report — real bug,
// caught live via screenshot comparison: Dashboard.js has its own,
// separate "Freeze this price" entry point on each pin card, which
// still went straight to the old single-step payment flow while
// DealScreen.js's own entry point correctly showed the new duration
// picker first. Root cause: FreezeDurationModal was originally defined
// as a local, unexported function inside DealScreen.js, so
// Dashboard.js had no way to reuse it without copy-pasting the whole
// component — exactly the kind of duplication that already caused one
// real production bug in this codebase before (see the "formatTime is
// not defined" comment elsewhere in this file's git history). One
// shared component, one shared tier list, imported by both entry
// points now, so they can't drift out of sync with each other again.
export const FREEZE_DURATION_TIERS = [
  { hours: 48, label: '48 hours', fee: 5.99 },
  { hours: 120, label: '5 days', fee: 9.99 },
];

// Rebuilt (Sep 23, per Ali's report) to show each tier's REAL fee in
// the pin's own currency, fetched from GET /freezes/quote/{pin_id} —
// this modal previously showed a flat "$5.99"/"$9.99" regardless of
// currency, the exact same class of bug already fixed on the DealScreen
// pitch card and the Watch screen, just not yet fixed at the two
// places closest to actual payment. This is also the screen the pitch
// card's own footnote now points to ("you'll see the exact amount on
// the next screen"), so it has to actually deliver that.
//
// Quotes for BOTH tiers are fetched once, together, on mount — not
// lazily per tier — so a person comparing 48h vs 5-day sees real
// numbers for both immediately, not one real and one still loading.
// The chosen tier's quote is handed up via onChoose(hours, quote) so
// FreezePaymentModal reuses these exact numbers rather than fetching
// its own and risking a different rate landing between the two
// screens (mirrors the 15-minute quote cache create_freeze itself
// already relies on for the same reason).
function FreezeDurationModal({ pinId, onChoose, onCancel, departureStopTrackingAt }) {
  const [quotes, setQuotes] = useState(null);
  const [quoteError, setQuoteError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setQuotes(null); setQuoteError(false);
    Promise.all(FREEZE_DURATION_TIERS.map(t => getWatchQuote(pinId, t.hours).then(r => r.quote)))
      .then(results => { if (!cancelled) setQuotes(results); })
      .catch(() => { if (!cancelled) setQuoteError(true); });
    return () => { cancelled = true; };
  }, [pinId]);

  // Early-resolution notice (Sep 16, per Ali's report — the
  // departure-timing rework, the purchase-time transparency piece
  // specifically): if a tier's own full duration would run past when
  // tracking actually stops for this pin (departureStopTrackingAt —
  // see get_pins' own computation of this in trips.py), the watch
  // won't actually run its full nominal length; resolve_freezes_
  // nearing_departure will resolve it early instead. Telling someone
  // this BEFORE they pay is the whole point — otherwise a 5-day tier
  // silently behaving like a 2-day one reads as a bait-and-switch,
  // not a watch that did something sensible. (The earlier blanket,
  // unconditional version of this notice was removed Sep 23 — it fired
  // above every tier regardless of relevance and read as a catch that
  // applied to every purchase; only this per-tier, conditional version
  // stays, since it only appears when it's actually true.)
  const now = Date.now();
  const stopAt = departureStopTrackingAt ? new Date(departureStopTrackingAt).getTime() : null;

  const quoteByHours = (hours) => quotes && quotes.find(q => q.duration_hours === hours);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px',
          maxWidth: '420px', width: '100%', padding: '28px'
        }}
      >
        <div style={{ fontSize: '17px', fontWeight: 800, color: theme.textPrimary, marginBottom: '6px' }}>
          How long should we watch this price?
        </div>
        <div style={{ fontSize: '12.5px', color: theme.textMuted, marginBottom: '20px' }}>
          Pick whichever matches how much time you actually need to decide.
        </div>

        {quoteError && (
          <p style={{ fontSize: '12.5px', color: theme.danger, marginBottom: '16px', lineHeight: 1.5 }}>
            We couldn't get today's exchange rate — try again in a moment.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {FREEZE_DURATION_TIERS.map((tier, idx) => {
            const quote = quoteByHours(tier.hours);
            const wouldRunPastCutoff = stopAt != null && (now + tier.hours * 3600000) > stopAt;
            // Dedup added (Sep 21, per Ali's report — caught live: with
            // the 24h purchase floor + 12h cutoff, this is actually the
            // COMMON case for a near-term buy, not a rare edge case —
            // any departure under ~72h away truncates BOTH tiers to
            // the identical stopAt, since stopAt is one fixed absolute
            // moment regardless of which tier's nominal length exceeds
            // it. Whenever an earlier (cheaper) tier in this list also
            // runs past cutoff, every later tier is mathematically
            // guaranteed to resolve at that exact same instant — so
            // repeating the identical sentence under each one isn't
            // just noisy, it was actively hiding the real point: the
            // pricier tier buys zero extra watching time in that case.
            // Swapped for a short, comparative note instead.
            const matchingCheaperTier = wouldRunPastCutoff
              ? FREEZE_DURATION_TIERS.slice(0, idx).find(t => stopAt != null && (now + t.hours * 3600000) > stopAt)
              : null;
            return (
              <div key={tier.hours}>
                <div
                  onClick={() => quote && onChoose(tier.hours, quote)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 16px', cursor: quote ? 'pointer' : 'default', border: `2px solid ${theme.structural}`,
                    opacity: quote ? 1 : 0.55
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 800, color: theme.structural }}>{tier.label}</span>
                    <span style={{ fontSize: '12px', color: theme.textMuted }}>Checked every 2 hours</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
                    {quote ? (
                      <>
                        <span style={{ fontSize: '15px', fontWeight: 800, color: theme.structural, fontFamily: theme.fontMono }}>
                          {formatMoney(quote.fee_local)} {quote.currency}
                        </span>
                        {String(quote.currency || '').toUpperCase() !== 'USD' && (
                          <span style={{ fontSize: '12px', color: theme.textMuted }}>US${formatMoney(tier.fee)}</span>
                        )}
                      </>
                    ) : (
                      <span style={{ fontSize: '12px', color: theme.textMuted }}>&hellip;</span>
                    )}
                  </div>
                </div>
                {wouldRunPastCutoff && (
                  matchingCheaperTier ? (
                    // Softened (Sep 21, per Ali's report — real point:
                    // the red/bold treatment read as a warning telling
                    // someone not to pick this tier, when the actual
                    // point is just informational — let them decide for
                    // themselves, even knowing most people won't pick
                    // the pricier option once they see this. Same fact,
                    // neutral tone, matching the muted style every
                    // other note in this modal already uses.
                    <div style={{ fontSize: '12px', color: theme.textMuted, padding: '6px 4px 0' }}>
                      Resolves at the same time as {matchingCheaperTier.label} — the extra fee buys the same amount of watching, not more time.
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: theme.textMuted, padding: '6px 4px 0' }}>
                      Your flight departs before this watch would normally end — we'll resolve it early, by{' '}
                      {new Date(stopAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })},
                      {' '}so you still have time to book.
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>

        {/* Plain-language result rule (Sep 23, per Ali's report — same
            wording as the pitch card, so the same fact isn't explained
            two different ways one screen apart): real thresholds, both
            currencies, both tiers share one rule so this states it once
            rather than per-tier. Uses whichever quote has loaded first
            as the source (both tiers convert the same fixed US$5/US$20
            at the same rate, so either is equally correct). */}
        {quotes && quotes[0] && (
          <div style={{ fontSize: '12.5px', color: theme.textMuted, background: theme.bg, border: `1px solid ${theme.border}`, padding: '10px 12px', marginTop: '14px', lineHeight: 1.5 }}>
            Either way, you pay nothing if the price ends {formatMoney(quotes[0].threshold_up_local)} {quotes[0].currency}{usdParen(quotes[0].currency, quotes[0].threshold_up_usd)} or more above, or {formatMoney(quotes[0].threshold_down_local)} {quotes[0].currency}{usdParen(quotes[0].currency, quotes[0].threshold_down_usd)} or more below, today's price.
          </div>
        )}

        <p
          onClick={onCancel}
          style={{ textAlign: 'center', fontSize: '12.5px', color: theme.textMuted, marginTop: '18px', cursor: 'pointer' }}
        >
          Cancel
        </p>
      </div>
    </div>
  );
}

export default FreezeDurationModal;
